import { useCallback, useEffect, useRef, useState } from "react";
import shopApi from "../../../api/shopApi";
import { useContacts } from "../contexts/ContactsContext";
import { filterContactsByPermission } from "../utils/contactPermissions";

const PAGE_SIZE = 200;
const MAX_PAGES = 100; // safety net (tối đa 20k contacts/shop)

const unwrapContacts = (data) => {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.contacts)) return data.contacts;
  if (Array.isArray(data)) return data;
  return [];
};

/**
 * Load toàn bộ contacts 1 shop theo filter. Loop page cho tới khi
 * `hasMore === false` (hoặc trả ít hơn PAGE_SIZE) — không phân trang UI.
 */
const loadAllForShop = async (shopId, filterBody, signal) => {
  const all = [];
  let page = 1;
  let hasMore = true;
  while (hasMore && page <= MAX_PAGES) {
    const data = await shopApi.getContacts(
      shopId,
      0,
      page,
      PAGE_SIZE,
      filterBody,
      signal,
    );
    const rows = unwrapContacts(data);
    all.push(...rows);
    // Dừng khi BE trả thiếu page hoặc flag hasMore=false
    if (rows.length < PAGE_SIZE || data?.hasMore === false) {
      hasMore = false;
    } else {
      page += 1;
    }
  }
  return all;
};

/**
 * Hook load TẤT CẢ contacts cho tab Bạn bè / Nhóm. Không phân trang.
 * - Multi-shop qua Promise.all + dedupe theo (shopId, uid)
 * - Backend đã apply permission filter; client filter thêm defense-in-depth
 *
 * @param {Object} opts
 * @param {'friends'|'groups'|'all'} opts.category
 */
export const useContactsData = ({ category = "friends" } = {}) => {
  const { selectedShopIds, shops, user, search, filters } = useContacts();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    if (!selectedShopIds.length) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const filterBody = {
        staffId: user?._id,
        categories:
          category === "friends"
            ? ["friends"]
            : category === "groups"
              ? ["groups"]
              : [],
        staffIds: filters.staffIds || [],
        tagIds: filters.tagIds || [],
        tagZaloIds: filters.tagZaloIds || [],
        pipelineStageIds: filters.pipelineStageIds || [],
        sortMode: filters.sortMode || "recent",
      };

      const results = await Promise.all(
        selectedShopIds.map(async (shopId) => {
          try {
            const rows = await loadAllForShop(shopId, filterBody, ctrl.signal);
            return rows.map((c) => ({ ...c, shopId }));
          } catch (e) {
            if (e.name === "CanceledError" || e.name === "AbortError") throw e;
            console.error(`[useContactsData] shop ${shopId} error:`, e);
            return [];
          }
        }),
      );

      let merged = results.flat();

      // Dedupe theo (shopId, uid)
      const seen = new Set();
      merged = merged.filter((c) => {
        const key = `${c.shopId}:${c.uid}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Defense-in-depth permission filter
      merged = filterContactsByPermission(merged, user, shops);

      // Client-side search (Tên + SĐT)
      if (search && search.trim()) {
        const q = search.trim().toLowerCase();
        merged = merged.filter((c) => {
          const name = (c.name || c.zaloName || "").toLowerCase();
          const phone =
            c.phone || (Array.isArray(c.phoneList) ? c.phoneList[0] : "") || "";
          const phoneAll = [
            c.phone,
            ...(Array.isArray(c.phoneList) ? c.phoneList : []),
          ]
            .filter(Boolean)
            .join(",")
            .toLowerCase();
          return (
            name.includes(q) ||
            String(phone).toLowerCase().includes(q) ||
            phoneAll.includes(q)
          );
        });
      }

      setItems(merged);
    } catch (e) {
      if (e.name !== "CanceledError" && e.name !== "AbortError") {
        setError(e.message || "Lỗi tải danh sách");
      }
    } finally {
      setLoading(false);
    }
  }, [selectedShopIds, user, search, filters, category, shops]);

  useEffect(() => {
    load();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  return { items, loading, error, reload: load };
};

export default useContactsData;
